// src/pages/agent-api-view-chat.tsx
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  useAgentApiAgentList,
  useAgentApiSendMessage,
  useAgentApiSubscribeConversation,
  useAgentApiConversationWithMessages,
  useAgentApiConversation,
  useConversationMetadataAggregated,
  useResetConversationMetadata,
  useSendGreetingIfPendingWithPolling,
  Loadable,
  Agents,
  ConversationPartialResults,
} from "@heartcheck/shared-react";
import {
  Alert,
  Badge,
  Card,
  Flex,
  Loader,
  Text,
  TextAreaField,
  View,
} from "@aws-amplify/ui-react";
import { Container } from "../library/container";
import { ChatRendered } from "../library/chat/chat-rendered";
import { useSetRecoilState } from "recoil";
import { Agent, ConversationEvent } from "@heartcheck/shared";
import { Logger } from "aws-amplify";

Logger.LOG_LEVEL = "WARN" as any;

const DEFAULT_HINT = (
  process.env.REACT_APP_PROCESSING_HINT_TEXT || "Working on it… responding."
).trim();

/** Global wellness banner copy (EU focus) */
const WELLNESS_BANNER = (
  <>
    <strong>Educational only — not a medical device.</strong>{" "}
    This chat does <em>not</em> provide diagnosis, treatment, or medical
    decision support. If you have symptoms such as chest pain, trouble
    breathing, severe weakness, or confusion, call emergency services{" "}
    <strong>112</strong> (EU), <strong>911</strong> (USA) now or seek urgent care.
    For personal medical questions, contact a licensed clinician.
  </>
);

/** Safely read an optional carryover agent id without changing Conversation's typing */
function getCarryoverAgentId(conversation: unknown): string | undefined {
  const c = conversation as any;
  const maybeId = c?.carryoverSystem?.agentId;
  return typeof maybeId === "string" && !!maybeId ? maybeId : undefined;
}

/** Treat “lambda-style” user events as agent messages (for completion detection). */
function isLambdaAgentUserEvent(e: ConversationEvent | undefined): boolean {
  return (
    !!e &&
    e.sender === "user" &&
    typeof e.userId === "string" &&
    e.userId.startsWith("AppsyncAgentFunction-")
  );
}

/**
 * Normalize the hybrid return of useAgentApiAgentList()
 * into a Loadable-like object (same pattern as AIAgentNewChat).
 */
function useNormalizedAgentList(): Loadable<any> {
  const raw: any = useAgentApiAgentList();

  const stateCandidate: any =
    raw && raw.state ? raw.state : Array.isArray(raw) ? raw[0] : raw;

  if (stateCandidate && typeof stateCandidate.isUnloaded === "function") {
    return stateCandidate as Loadable<any>;
  }

  return Loadable.unloaded<any>();
}

/**
 * Extract an array of Agent objects from the Loadable value, regardless of
 * whether it’s an ObjRecord, array, or something with items()/values().
 */
function extractAgentsArray(agentList: Loadable<any>): Agent[] {
  const value = agentList.value;
  if (!value) return [];

  if (typeof value.items === "function") {
    return (value.items() ?? []) as Agent[];
  }

  if (Array.isArray(value)) {
    return value as Agent[];
  }

  if (typeof value.values === "function") {
    return Array.from(value.values()) as Agent[];
  }

  return [];
}

export function AIAgentViewChat() {
  const params = useParams<{ chatId: string }>();
  const rawChatId = params?.chatId;
  const chatId = rawChatId ?? "";

  const conversationObject = useAgentApiConversation(chatId);

  // ✅ Hybrid-safe unwrapping of agent list (matches NewChat logic)
  const agentList = useNormalizedAgentList();
  const agentArray = useMemo(() => extractAgentsArray(agentList), [agentList]);
  const isAgentListLoaded = agentList.isLoaded();

  const conversation = conversationObject.value;

  // Map of agentId → Agent
  const agents: Record<string, Agent> = useMemo(() => {
    return agentArray.reduce<Record<string, Agent>>((acc, a) => {
      if (a && typeof a.id === "string") acc[a.id] = a;
      return acc;
    }, {});
  }, [agentArray]);

  const isConversationLoaded = conversationObject.isLoaded();

  // useAgentApiConversationWithMessages is hybrid but we use object-style access
  const internal = useAgentApiConversationWithMessages(chatId);
  const internalEvents = internal.events ?? [];
  const loadingConversation = internal.loadingConversation;

  const aggregatedMetaRaw = useConversationMetadataAggregated(chatId);
  const convMeta =
    aggregatedMetaRaw && typeof aggregatedMetaRaw === "object"
      ? aggregatedMetaRaw.meta
      : undefined;
  const metaAgentId =
    aggregatedMetaRaw && typeof aggregatedMetaRaw === "object"
      ? aggregatedMetaRaw.agentId
      : undefined;

  const lastEventAgentId = internalEvents.length
    ? internalEvents[internalEvents.length - 1]?.agentId
    : undefined;

  const activeAgentId: string | undefined = useMemo(() => {
    const carry = getCarryoverAgentId(conversation);
    return metaAgentId ?? carry ?? (conversation as any)?.agent ?? lastEventAgentId;
  }, [metaAgentId, conversation, lastEventAgentId]);

  const partialText = (convMeta?.agentPartialMessage || "").trim();
  const hasPartial = partialText.length > 0;

  const stopForActive =
    metaAgentId && activeAgentId
      ? metaAgentId === activeAgentId && !!convMeta?.agentStopResponding
      : !!convMeta?.agentStopResponding;

  const resetConversationMetadata = useResetConversationMetadata();
  const sendMessage = useAgentApiSendMessage();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const setAgentState = useSetRecoilState(Agents);
  const setPartialResults = useSetRecoilState(ConversationPartialResults);

  const [chatString, setChatString] = useState<string>("");

  const lastSubmitAtRef = useRef<number>(0);

  /**
   * ✅ IMPORTANT FIX:
   * Do NOT send an extra pending greeting here.
   * Your shared hook already does this safely (and matches NewChat behavior).
   * The previous custom effect that sent actionRequested:"start" was causing:
   * - duplicate "hello" events
   * - wrong routing/signal for QnA agent activation
   */
  const waitResults = useSendGreetingIfPendingWithPolling({
    chatId,
    conversation,
    isConversationLoaded,
    agents,
    isAgentListLoaded,
    extraTrigger: 0,
  });

  const confirmedEvents = useMemo<ConversationEvent[]>(
    () => waitResults.events ?? [],
    [waitResults.events]
  );
  const loadingEvents = waitResults.loading;
  const pollNow = waitResults.pollNow;

  const useConfirmed = internalEvents.length === 0 && confirmedEvents.length > 0;
  const externalEventsProp: ConversationEvent[] | undefined = useConfirmed
    ? confirmedEvents
    : undefined;

  const [renderedCount, setRenderedCount] = useState(0);

  const anyEventsCount = Math.max(confirmedEvents.length, internalEvents.length);
  const effectiveCount = Math.max(anyEventsCount, renderedCount);

  const sourceEvents = externalEventsProp ?? internalEvents;

  const hasAnswerForThisTurn = useMemo(() => {
    const cutoff = lastSubmitAtRef.current || 0;
    if (!cutoff) return false;

    for (let i = sourceEvents.length - 1; i >= 0; i--) {
      const e = sourceEvents[i];
      const t = new Date(e.timestamp).getTime();
      if (t < cutoff) break;

      const isAgentish = e.sender === "agent" || isLambdaAgentUserEvent(e as any);
      if (
        isAgentish &&
        (e.event?.message || e.event?.actionResult || e.event?.innerDialog)
      ) {
        return true;
      }
    }
    return false;
  }, [sourceEvents]);

  const [sending, setSending] = useState(false);
  const [showLocalHint, setShowLocalHint] = useState(false);

  const isRespondingMeta =
    !stopForActive &&
    (hasPartial ||
      (convMeta?.agentStartResponding === true && !hasAnswerForThisTurn));

  const isResponding = isRespondingMeta || sending;

  const [isRespondingLatched, setIsRespondingLatched] = useState(false);
  useEffect(() => {
    let t: any;
    if (isResponding) {
      setIsRespondingLatched(true);
    } else {
      t = setTimeout(() => setIsRespondingLatched(false), 400);
    }
    return () => {
      if (t) clearTimeout(t);
    };
  }, [isResponding]);

  const statusText = hasPartial ? partialText : DEFAULT_HINT;

  const [metaTrigger, setMetaTrigger] = useState(0);
  const prevRespondingRef = useRef<boolean | null>(null);
  useEffect(() => {
    const prev = prevRespondingRef.current;
    const curr = isRespondingMeta;
    if (prev === true && curr === false && chatId) {
      setMetaTrigger((t) => t + 1);
    }
    prevRespondingRef.current = curr;
  }, [isRespondingMeta, chatId]);

  const lastPollRef = useRef(0);
  const safePollNow = useCallback(() => {
    const now = Date.now();
    if (now - lastPollRef.current < 900) return;
    lastPollRef.current = now;
    try {
      if (typeof pollNow === "function") pollNow();
    } catch {
      /* ignore */
    }
  }, [pollNow]);

  useEffect(() => {
    if (metaTrigger > 0) safePollNow();
  }, [metaTrigger, safePollNow]);

  const showSpinner =
    effectiveCount === 0 &&
    (loadingEvents || loadingConversation) &&
    !sending &&
    !isRespondingMeta;

  useEffect(() => {
    if (!sending) return;
    if (
      convMeta?.agentStartResponding === true ||
      hasPartial ||
      hasAnswerForThisTurn ||
      stopForActive
    ) {
      setSending(false);
    }
  }, [
    sending,
    convMeta?.agentStartResponding,
    hasPartial,
    hasAnswerForThisTurn,
    stopForActive,
  ]);

  useEffect(() => {
    if (!showLocalHint) return;
    if (hasAnswerForThisTurn || stopForActive) {
      setShowLocalHint(false);
    }
  }, [showLocalHint, hasAnswerForThisTurn, stopForActive]);

  useEffect(() => {
    if (!chatId) return;
    if (convMeta?.agentStartResponding) {
      safePollNow();
    }
  }, [chatId, convMeta?.agentStartResponding, safePollNow]);

  useEffect(() => {
    setAgentState(Loadable.unloaded());
    setShowLocalHint(false);
    lastSubmitAtRef.current = 0;
  }, [setAgentState, chatId]);

  useAgentApiSubscribeConversation(chatId);

  useEffect(() => {
    if (!chatId) return;

    const shouldClear =
      ((metaAgentId && activeAgentId ? metaAgentId === activeAgentId : true) &&
        convMeta?.agentStopResponding === true) ||
      hasAnswerForThisTurn;

    if (
      shouldClear &&
      (convMeta?.agentPartialMessage ?? "").trim().length > 0
    ) {
      const t = setTimeout(() => resetConversationMetadata(chatId), 250);
      return () => clearTimeout(t);
    }
    return;
  }, [
    chatId,
    activeAgentId,
    metaAgentId,
    convMeta?.agentStopResponding,
    convMeta?.agentPartialMessage,
    hasAnswerForThisTurn,
    resetConversationMetadata,
  ]);

  const agentName =
    (typeof activeAgentId === "string" && agents[activeAgentId]?.name) ||
    activeAgentId ||
    "Unknown Agent";

  const canSend = !!chatId && !!chatString.trim();

  const handleSubmit = useCallback(async () => {
    if (!canSend) return;

    const msg = chatString.trim();

    lastSubmitAtRef.current = Date.now();

    setSending(true);
    setShowLocalHint(true);

    setChatString("");

    requestAnimationFrame(() => inputRef.current?.focus());

    if (chatId && activeAgentId) {
      setPartialResults((prev) => {
        const conv =
          prev[chatId] ??
          ({
            activeAgentId: undefined as string | undefined,
            byAgent: {},
          } as any);

        const nextActiveId: string | undefined =
          typeof conv.activeAgentId === "string"
            ? conv.activeAgentId
            : typeof activeAgentId === "string"
              ? activeAgentId
              : undefined;

        const prevMeta =
          conv.byAgent[nextActiveId || (activeAgentId as string)] ?? {};

        return {
          ...prev,
          [chatId]: {
            activeAgentId: nextActiveId,
            byAgent: {
              ...conv.byAgent,
              [activeAgentId as string]: {
                ...prevMeta,
                agentStartResponding: true,
                agentStopResponding: false,
                agentPartialMessage: DEFAULT_HINT,
                updatedAt: new Date().toISOString(),
              },
            },
          },
        };
      });
    }

    try {
      await sendMessage({
        chatId,
        event: { message: msg },
      });

      setTimeout(() => {
        safePollNow();
      }, 150);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error("❌ sendMessage failed:", err);

      setTimeout(() => {
        if (
          !convMeta?.agentStartResponding &&
          !hasPartial &&
          !hasAnswerForThisTurn
        ) {
          setSending(false);
          setShowLocalHint(false);
        }
      }, 6000);
    }
  }, [
    canSend,
    chatString,
    chatId,
    activeAgentId,
    setPartialResults,
    sendMessage,
    safePollNow,
    convMeta?.agentStartResponding,
    hasPartial,
    hasAnswerForThisTurn,
  ]);

  const submittingRef = useRef(false);

  return (
    <Flex direction="column" width="100%" alignItems="flex-start">
      <View
        width="100%"
        maxWidth="700px"
        style={{ marginLeft: "0", paddingLeft: "16px" }}
      >
        <Alert
          variation="info"
          isDismissible={false}
          role="status"
          marginBottom="12px"
          borderRadius="8px"
        >
          {WELLNESS_BANNER}{" "}
          <Badge size="small" variation="warning" marginLeft="6px">
            Wellness / Educational
          </Badge>
        </Alert>

        <Container
          heading={`Chatting with '${agentName}'`}
          minHeight={500}
          padBody={0}
        >
          <ChatRendered
            events={externalEventsProp}
            onRenderedCount={setRenderedCount}
          />

          {showSpinner && (
            <>
              <Loader variation="linear" />
              <Text marginTop={8} style={{ fontSize: "0.9rem" }}>
                Connecting to the agent…
              </Text>
            </>
          )}
        </Container>

        <Card
          marginTop={20}
          padding={16}
          width="100%"
          maxWidth="700px"
          alignSelf="flex-start"
        >
          {(isRespondingLatched || showLocalHint) && (
            <Text
              marginTop={2}
              style={{ fontSize: "0.9rem", opacity: 0.8 }}
              aria-live="polite"
            >
              {statusText}
            </Text>
          )}

          <TextAreaField
            key={chatId}
            labelHidden
            label="Message"
            placeholder="Type your message here"
            ref={inputRef}
            aria-busy={isRespondingLatched || showLocalHint}
            onKeyDown={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (submittingRef.current) return;
                submittingRef.current = true;
                try {
                  await handleSubmit();
                } finally {
                  submittingRef.current = false;
                }
              }
            }}
            value={chatString}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setChatString(e.target.value)
            }
            rows={3}
            style={{ width: "100%", resize: "vertical" }}
          />
        </Card>
      </View>
    </Flex>
  );
}
